import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Loader2,
  File,
  Image,
  Settings,
  Home,
  Layers,
  Box,
  Zap,
  Droplet,
  Eye
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ExtendedLead } from './types';

interface Projeto {
  id: string;
  nome_arquivo: string;
  nome_original: string;
  tipo_projeto: string;
  tipo_arquivo: string;
  tamanho_bytes: number;
  descricao?: string;
  created_at: string;
}

interface ProjetosProps {
  leadData: ExtendedLead;
  onUpdate: () => void;
}

const TIPOS_PROJETO = [
  { value: 'Projeto Elétrico', label: 'Projeto Elétrico', icon: Zap },
  { value: 'Diagrama Unifilar', label: 'Diagrama Unifilar', icon: Layers },
  { value: 'Memorial Descritivo', label: 'Memorial Descritivo', icon: FileText },
  { value: 'Outros', label: 'Outros', icon: File },
];

export default function Projetos({ leadData, onUpdate }: ProjetosProps) {
  const [projetos, setProjetos] = useState<Projeto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projetoToDelete, setProjetoToDelete] = useState<Projeto | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [tipoProjeto, setTipoProjeto] = useState('Projeto Elétrico');
  const [descricao, setDescricao] = useState('');
  const { toast } = useToast();

  const fetchProjetos = async () => {
    try {
      setLoading(true);
      console.log('📁 fetchProjetos - Buscando projetos para lead:', leadData.id);

      const response = await fetchWithAuth(`/api/cliente-projetos/${leadData.id}`);
      console.log('📁 fetchProjetos - Resposta recebida:', { status: response.status, ok: response.ok });

      if (!response.ok) {
        // Tentar ler como JSON primeiro
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Erro ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
      }

      const data = await response.json();
      setProjetos(data.projetos || []);
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os projetos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leadData.id) {
      fetchProjetos();
    }
  }, [leadData.id]);

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Selecione pelo menos um arquivo',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      console.log('📤 handleUpload - Iniciando upload de projeto:', { 
        leadId: leadData.id, 
        filesCount: selectedFiles?.length,
        tipoProjeto,
        descricao 
      });

      const formData = new FormData();
      Array.from(selectedFiles).forEach(file => {
        formData.append('projetos', file);
      });
      formData.append('leadId', leadData.id);
      formData.append('tipoProjeto', tipoProjeto);
      if (descricao) {
        formData.append('descricao', descricao);
      }

      const response = await fetchWithAuth('/api/cliente-projetos/upload', {
        method: 'POST',
        body: formData,
      });
      console.log('📤 handleUpload - Resposta recebida:', { status: response.status, ok: response.ok });

      if (!response.ok) {
        // Tentar ler como JSON primeiro
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Erro ao fazer upload';
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            errorMessage = `Erro ${response.status}: ${response.statusText}`;
          }
        } else {
          errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      toast({
        title: 'Sucesso',
        description: 'Projeto(s) enviado(s) com sucesso',
      });

      // Limpar formulário
      setSelectedFiles(null);
      setTipoProjeto('Projeto Elétrico');
      setDescricao('');
      setUploadDialogOpen(false);
      
      // Atualizar lista
      fetchProjetos();
      onUpdate();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao fazer upload',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (projeto: Projeto) => {
    try {
      const response = await fetchWithAuth(`/api/cliente-projetos/download/${projeto.id}`);

      if (!response.ok) {
        throw new Error('Erro ao gerar link de download');
      }

      const data = await response.json();
      
      // Abrir URL em nova aba
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível fazer o download do projeto',
        variant: 'destructive',
      });
    }
  };

  const handleView = async (projeto: Projeto) => {
    try {
      const response = await fetchWithAuth(`/api/cliente-projetos/download/${projeto.id}`);

      if (!response.ok) {
        throw new Error('Erro ao gerar link de visualização');
      }

      const data = await response.json();
      
      // Abrir URL em nova aba para visualização
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Erro ao visualizar projeto:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível visualizar o projeto',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!projetoToDelete) return;

    try {
      const response = await fetchWithAuth(`/api/cliente-projetos/${projetoToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar projeto');
      }

      toast({
        title: 'Sucesso',
        description: 'Projeto deletado com sucesso',
      });

      setDeleteDialogOpen(false);
      setProjetoToDelete(null);
      fetchProjetos();
      onUpdate();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível deletar o projeto',
        variant: 'destructive',
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileIcon = (tipoArquivo: string, nomeArquivo: string) => {
    const extensao = nomeArquivo.split('.').pop()?.toLowerCase();
    
    // Arquivos CAD e projetos
    if (extensao === 'dwg') return <Home className="h-5 w-5 text-blue-600" />;
    if (extensao === 'skp') return <Box className="h-5 w-5 text-orange-500" />;
    if (extensao === 'rvt') return <Layers className="h-5 w-5 text-green-600" />;
    if (extensao === 'max') return <Settings className="h-5 w-5 text-purple-500" />;
    if (extensao === 'blend') return <Settings className="h-5 w-5 text-orange-400" />;
    
    // Arquivos de imagem
    if (tipoArquivo.includes('image')) return <Image className="h-5 w-5 text-blue-500" />;
    
    // PDFs
    if (tipoArquivo.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    
    // Outros arquivos
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const getTipoIcon = (tipo: string) => {
    const tipoData = TIPOS_PROJETO.find(t => t.value === tipo);
    if (!tipoData) return <FileText className="h-4 w-4" />;
    const Icon = tipoData.icon;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
  return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

              return (
    <div className="space-y-6">
      {/* Projetos */}
      <div>
        {/* Header com botão de upload */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Projetos</h3>
            <p className="text-sm text-muted-foreground">
              Gerencie os projetos do cliente
            </p>
          </div>
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Enviar Projeto
          </Button>
        </div>

        {/* Lista de projetos */}
        {projetos.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Home className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum projeto encontrado</h3>
              <p className="text-muted-foreground text-center max-w-md mb-4">
                Este cliente ainda não possui projetos cadastrados. Clique no botão acima para enviar projetos.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {projetos.map((projeto) => (
              <Card key={projeto.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getFileIcon(projeto.tipo_arquivo, projeto.nome_original)}
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm whitespace-normal break-words">
                          {projeto.nome_original}
                        </CardTitle>
                        <CardDescription className="text-xs flex items-center gap-1 mt-1">
                          {getTipoIcon(projeto.tipo_projeto)}
                          <span>{projeto.tipo_projeto}</span>
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Tamanho: {formatFileSize(projeto.tamanho_bytes)}</p>
                    <p>Enviado em: {formatDate(projeto.created_at)}</p>
                    {projeto.descricao && (
                      <p className="italic">"{projeto.descricao}"</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(projeto)}
                      className="flex-1"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Baixar
                    </Button>
                    {(projeto.tipo_arquivo.includes('pdf') || projeto.nome_original.toLowerCase().endsWith('.pdf')) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(projeto)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setProjetoToDelete(projeto);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog de Upload */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar Projeto</DialogTitle>
            <DialogDescription>
              Selecione um ou mais arquivos de projeto para enviar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo de Projeto</Label>
              <Select value={tipoProjeto} onValueChange={setTipoProjeto}>
                <SelectTrigger id="tipo">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_PROJETO.map((tipo) => {
                    const Icon = tipo.icon;
                    return (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {tipo.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Input
                id="descricao"
                placeholder="Ex: Projeto arquitetônico - Casa térrea"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="arquivos">Arquivos</Label>
              <Input
                id="arquivos"
                type="file"
                multiple
                accept=".dwg,.skp,.rvt,.max,.blend,.pdf,.jpg,.jpeg,.png,.zip,.rar"
                onChange={(e) => setSelectedFiles(e.target.files)}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="arquivos"
                  className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary font-medium py-2 px-4 rounded-md inline-block transition-colors"
                >
                  Escolher Arquivos
                </Label>
                <div className="text-sm text-muted-foreground min-w-0 flex-1">
                  {selectedFiles && selectedFiles.length > 0 ? (
                    <span className="block whitespace-normal break-words">
                      {selectedFiles.length === 1
                        ? selectedFiles[0].name
                        : `${selectedFiles.length} arquivos selecionados`}
                    </span>
                  ) : (
                    <span className="block whitespace-normal break-words">Nenhum arquivo selecionado</span>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: DWG, SKP, RVT, MAX, BLEND, PDF, JPG, PNG, ZIP, RAR (máx. 50MB por arquivo)
              </p>
            </div>
            {selectedFiles && selectedFiles.length > 0 && (
              <div className="bg-muted p-3 rounded-md min-w-0">
                <p className="text-sm font-medium mb-2">
                  {selectedFiles.length} arquivo(s) selecionado(s):
                </p>
                <ul className="text-xs space-y-1">
                  {Array.from(selectedFiles).map((file, index) => (
                    <li key={index} className="whitespace-normal break-words">
                      • {file.name} ({formatFileSize(file.size)})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setSelectedFiles(null);
                setDescricao('');
              }}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFiles}>
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o projeto "{projetoToDelete?.nome_original}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setProjetoToDelete(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}